// @flow strict

import * as React from 'react';
import { useFormikContext } from 'formik';
import { FormGridCell, FormGridRow, TextInput, Checkbox } from '@ansarada/ace-react';
import styles from './styles.scss';

import LocationInput from '../LocationInput';
import TermsAndConditions from '../TermsAndConditions';
import {
  raiseFilledEventIncludeData,
  raiseFilledEventExcludeData,
  SupportedTrackingFieldNames,
} from '../../../../core/lib/segment-actions';
import type { FormValue } from '../types';
import { FirstNameMaxLength, LastNameMaxLength, PROFILE_FORM_KEYS as KEYS } from '../constants';

type FormInputProps = {
  isSubmitting: boolean,
  isProfileExists: boolean,
};

export default function UserProfileInputs({ isSubmitting, isProfileExists }: FormInputProps) {
  const { values, setFieldValue, errors, touched, setFieldTouched } = useFormikContext<FormValue>();

  const isShowError = (fieldName: string): boolean =>
    !!(touched && touched[fieldName] && errors && errors[fieldName]);

  const passwordError = () => {
    if (isShowError(KEYS.password) && errors && errors.password) {
      return [
        errors.password,
        'Hint: Password must contain at least 8 characters, including a lowercase letter, an uppercase letter, and a number. It cannot contain part of your email.',
      ];
    }
    return undefined;
  };

  if (isProfileExists) {
    return (
      <div className={styles.inputGroups}>
        <FormGridRow key={KEYS.password}>
          <FormGridCell
            errors={isShowError(KEYS.password) ? <span>{errors.password}</span> : undefined}
            errorPosition="Bottom"
          >
            <TextInput
              data-test-id="password"
              data-ansarada-ccd
              defaultType="Password"
              autoComplete="off"
              className={styles.passwordInput}
              value={values.password}
              onChangeValue={value => setFieldValue(KEYS.password, value)}
              status={isShowError(KEYS.password) ? 'Bad' : undefined}
              disabled={isSubmitting}
              onBlur={() =>
                raiseFilledEventExcludeData(
                  values.password,
                  SupportedTrackingFieldNames.ExcludeData.Password,
                )
              }
            />
          </FormGridCell>
        </FormGridRow>

        <FormGridRow key={KEYS.termsAndConditionsAccepted}>
          <FormGridCell>
            <Checkbox
              data-test-id={KEYS.termsAndConditionsAccepted}
              name={KEYS.termsAndConditionsAccepted}
              label={
                <span>
                  <TermsAndConditions />
                </span>
              }
              value={String(values.termsAndConditionsAccepted)}
              checked={values.termsAndConditionsAccepted}
              onChecked={checked => setFieldValue(KEYS.termsAndConditionsAccepted, checked)}
            />
          </FormGridCell>
        </FormGridRow>
      </div>
    );
  }

  return (
    <div className={styles.inputGroups}>
      <FormGridRow key={KEYS.firstName}>
        <FormGridCell
          labelText="First name"
          errors={isShowError(KEYS.firstName) ? <span>{errors.firstName}</span> : undefined}
          errorPosition="Bottom"
        >
          <TextInput
            data-test-id={KEYS.firstName}
            name={KEYS.firstName}
            data-ansarada-ccd
            type="Text"
            autoComplete="off"
            maxLength={FirstNameMaxLength}
            value={values.firstName}
            onChangeValue={value => setFieldValue(KEYS.firstName, value)}
            status={isShowError(KEYS.firstName) ? 'Bad' : undefined}
            disabled={isSubmitting}
            onBlur={() => {
              setFieldTouched(KEYS.firstName);
              raiseFilledEventExcludeData(
                values.firstName,
                SupportedTrackingFieldNames.ExcludeData.FirstName,
              );
            }}
          />
        </FormGridCell>
      </FormGridRow>

      <FormGridRow key={KEYS.lastName}>
        <FormGridCell
          labelText="Last name"
          errors={isShowError(KEYS.lastName) ? <span>{errors.lastName}</span> : undefined}
          errorPosition="Bottom"
        >
          <TextInput
            data-test-id={KEYS.lastName}
            name={KEYS.lastName}
            data-ansarada-ccd
            type="Text"
            autoComplete="off"
            maxLength={LastNameMaxLength}
            defaultValue={values.lastName}
            onChangeValue={value => setFieldValue(KEYS.lastName, value)}
            status={isShowError(KEYS.lastName) ? 'Bad' : undefined}
            disabled={isSubmitting}
            onBlur={() => {
              setFieldTouched(KEYS.lastName);
              raiseFilledEventExcludeData(
                values.lastName,
                SupportedTrackingFieldNames.ExcludeData.LastName,
              );
            }}
          />
        </FormGridCell>
      </FormGridRow>

      <FormGridRow key={KEYS.locationId}>
        <FormGridCell>
          <LocationInput
            isShowError={isShowError}
            disabled={isSubmitting}
            onBlur={selectedLocation => {
              setFieldTouched(KEYS.locationId);
              raiseFilledEventIncludeData(
                selectedLocation,
                SupportedTrackingFieldNames.IncludeData.Location,
              );
            }}
          />
        </FormGridCell>
      </FormGridRow>

      <FormGridRow key={KEYS.phoneNumber}>
        <FormGridCell
          labelText="Phone"
          errors={isShowError(KEYS.phoneNumber) ? <span>{errors.phoneNumber}</span> : undefined}
          errorPosition="Bottom"
        >
          <TextInput
            data-test-id={KEYS.phoneNumber}
            name={KEYS.phoneNumber}
            data-ansarada-ccd
            type="Tel"
            autoComplete="off"
            value={values.phoneNumber}
            onChangeValue={value => setFieldValue(KEYS.phoneNumber, value)}
            status={isShowError(KEYS.phoneNumber) ? 'Bad' : undefined}
            disabled={isSubmitting}
            onBlur={() => {
              setFieldTouched(KEYS.phoneNumber);
              raiseFilledEventExcludeData(
                values.phoneNumber,
                SupportedTrackingFieldNames.ExcludeData.PhoneNumber,
              );
            }}
          />
        </FormGridCell>
      </FormGridRow>

      <FormGridRow key={KEYS.password}>
        <FormGridCell labelText="Password" errors={passwordError()} errorPosition="Bottom">
          <TextInput
            data-test-id={KEYS.password}
            data-ansarada-ccd
            defaultType="Password"
            autoComplete="off"
            className={styles.passwordInput}
            value={values.password}
            onChangeValue={value => setFieldValue(KEYS.password, value)}
            status={isShowError(KEYS.password) ? 'Bad' : undefined}
            disabled={isSubmitting}
            onBlur={() => {
              setFieldTouched(KEYS.password);
              raiseFilledEventExcludeData(
                values.password,
                SupportedTrackingFieldNames.ExcludeData.Password,
              );
            }}
          />
        </FormGridCell>
      </FormGridRow>

      <FormGridRow key={KEYS.termsAndConditionsAccepted}>
        <FormGridCell>
          <Checkbox
            data-test-id={KEYS.termsAndConditionsAccepted}
            name={KEYS.termsAndConditionsAccepted}
            label={
              <span>
                <TermsAndConditions />
              </span>
            }
            value={String(values.termsAndConditionsAccepted)}
            checked={values.termsAndConditionsAccepted}
            onChecked={checked => setFieldValue(KEYS.termsAndConditionsAccepted, checked)}
          />
        </FormGridCell>
      </FormGridRow>
    </div>
  );
}
